'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useFollowUpWorkflow } from '@/lib/services/follow-up/workflow-hooks';
import { useMatterFilesService } from '@/lib/services/files/hooks';
import { SendFollowUpStep } from './steps/send-follow-up-step';
import { FollowUpWorkflowSkeleton } from './follow-up-workflow-skeleton';
import type { FollowUpWorkflowProps } from './types';

interface ExtendedFollowUpWorkflowProps extends FollowUpWorkflowProps {
  showEmailOnly?: boolean;
}

export function FollowUpWorkflowWithDialog({
  matterId,
  institutionId,
  parentCorrespondenceId,
  onComplete,
  showEmailOnly = true
}: ExtendedFollowUpWorkflowProps) {
  const [addSenderAsCc, setAddSenderAsCc] = useState(true);
  const router = useRouter();

  const {
    isCreatingDraft,
    draftCreationError,
    correspondenceId,
    correspondence,
    isLoadingCorrespondence,
    correspondenceError,
    workflowData,
    isLoadingWorkflow,
    workflowError,
    updateCorrespondence,
    sendCorrespondence,
    refetchData,
    isSending,
    sendError
  } = useFollowUpWorkflow({
    matterId,
    institutionId,
    parentCorrespondenceId
  });

  // Fetch real matter files for attachments
  const { files: matterFiles } = useMatterFilesService(matterId);

  // Validate required props (after all hooks have been called).
  if (!matterId || !institutionId || !parentCorrespondenceId) {
    return (
      <>
        <DialogTitle>Send Follow-up</DialogTitle>
        <div className="container mx-auto max-w-6xl p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Missing required information. Please ensure the matter,
              institution, and parent correspondence are properly configured.
            </AlertDescription>
          </Alert>
        </div>
      </>
    );
  }

  // Optimistic send handler - closes dialog immediately on successful API response
  const handleSendWithOptimisticClose = async () => {
    try {
      await sendCorrespondence({ addSenderAsCc });
      // Close immediately - the toast has already been triggered by sendCorrespondence
      onComplete?.({ correspondenceId, institutionId });
    } catch (error) {
      // Error is already handled by the sendCorrespondence function
      // Just don't close the dialog if there's an error
      console.error('Send failed, keeping dialog open:', error);
    }
  };

  // Handle successful send - auto-close dialog for follow-up workflow only
  // Note: Notification and collection workflows don't auto-close after sending
  // Removed - now handled optimistically in handleSendWithOptimisticClose above

  // Show loading state while creating draft
  if (isCreatingDraft) {
    return (
      <>
        <DialogTitle>Preparing Follow-up</DialogTitle>
        <FollowUpWorkflowSkeleton message="Creating follow-up draft..." />
      </>
    );
  }

  // Show error if draft creation failed
  if (draftCreationError) {
    return (
      <>
        <DialogTitle>Send Follow-up</DialogTitle>
        <div className="container mx-auto max-w-6xl p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <div>Failed to create follow-up draft:</div>
                <div className="text-sm">{draftCreationError.message}</div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => router.refresh()}
                  className="mt-2"
                >
                  Try Again
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </>
    );
  }

  // Show loading state while fetching data
  if (isLoadingCorrespondence || isLoadingWorkflow) {
    return (
      <>
        <DialogTitle>Loading Follow-up</DialogTitle>
        <FollowUpWorkflowSkeleton message="Loading correspondence data..." />
      </>
    );
  }

  // Show error if data fetching failed
  if (correspondenceError || workflowError) {
    const error = correspondenceError || workflowError;
    return (
      <>
        <DialogTitle>Send Follow-up</DialogTitle>
        <div className="container mx-auto max-w-6xl p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <div>Failed to load correspondence data:</div>
                <div className="text-sm">{error?.message}</div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetchData()}
                  className="mt-2"
                >
                  Try Again
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </>
    );
  }

  // Check if we have the necessary data
  if (!correspondenceId || !correspondence || !workflowData) {
    return (
      <>
        <DialogTitle>Send Follow-up</DialogTitle>
        <div className="container mx-auto max-w-6xl p-6">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Loading correspondence data...</AlertDescription>
          </Alert>
        </div>
      </>
    );
  }

  // Transform matter files to workflow document format
  const enhancedWorkflowData = {
    ...workflowData,
    documents: matterFiles.map((file) => {
      let docType:
        | 'form'
        | 'death-certificate'
        | 'will'
        | 'other'
        | 'grant-of-probate'
        | 'lpr-id' = 'other';
      const fileType =
        typeof file.fileType === 'string'
          ? file.fileType
          : file.fileType?.slug || '';

      if (fileType.includes('death-certificate')) docType = 'death-certificate';
      else if (fileType.includes('will')) docType = 'will';
      else if (fileType.includes('grant') || fileType.includes('probate'))
        docType = 'grant-of-probate';
      else if (
        fileType.includes('lpr-id') ||
        fileType.includes('identification')
      )
        docType = 'lpr-id';
      else if (fileType.includes('form')) docType = 'form';

      const fileName =
        file.displayName || file.originalFilename || `File ${file.id}`;

      return {
        id: file.id,
        type: docType,
        fileName: fileName,
        uploadDate: file.createdAt,
        isCertified: file.isCertifiedCopy || false,
        fileUrl: `/api/files/${file.id}/download`
      };
    })
  };

  return (
    <div className="space-y-6">
      <div>
        <DialogTitle>Send Follow-up Correspondence</DialogTitle>
        <DialogDescription>
          Review and send your follow-up correspondence to{' '}
          {workflowData.institutions[0]?.name}
        </DialogDescription>
      </div>

      <div className="container mx-auto max-w-6xl space-y-8">
        <SendFollowUpStep
          matterId={matterId}
          institutionId={institutionId}
          correspondenceId={correspondenceId}
          workflowData={enhancedWorkflowData}
          correspondence={correspondence}
          onSend={handleSendWithOptimisticClose}
          onUpdate={updateCorrespondence}
          onRefresh={refetchData}
          isSending={isSending}
          sendError={sendError}
          showEmailOnly={showEmailOnly}
          addSenderAsCc={addSenderAsCc}
          onAddSenderAsCcChange={setAddSenderAsCc}
        />
      </div>
    </div>
  );
}
